import { useEffect, useState, type ReactNode } from "react";
import { DataTable } from "../components/DataTable/index.tsx";
import { MoreLinks } from "../components/MoreLinks.tsx";
import { i18n } from "../util/useT.ts";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { getCols } from "../util/getCols.ts";
import type { View } from "../../../../common/types.ts";
import { wrappedAgeAtDeath } from "../components/AgeAtDeath.tsx";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import type { DataTableRow } from "../components/DataTable/index.tsx";
import { PlusMinus } from "../components/PlusMinus.tsx";
import { TeamAbbrevText } from "../components/TeamAbbrevLink.tsx";
import { wrappedPosition } from "../util/positionNames.tsx";
import { filterPolicyKeys, playerStatsDefaultStats } from "../mobile/displayPolicies.ts";
import {
  MobileContentHero,
  MobileEntityRow,
  MobileExpertDisclosure,
  MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import { MobileStatInsights } from "../mobile/MobileStatInsights.tsx";
import {
  getInsightStatValue,
  getTeamTopScorer,
  getTeamTopScorerInsight,
} from "../mobile/statInsights.ts";
import {
  getP3V1LeagueLeadingMetrics,
  getP3V1PlayerStatsTrendContext,
} from "../util/statInsight.ts";
import {
  getLeaderboardMeaningfulness,
  getPreviousSeasonReviewLabel,
  getStatAccumulatingMessage,
} from "../mobile/statMeaningfulness.ts";

type DataTableCellObject = {
  value: ReactNode;
};

const isDataTableCellObject = (value: DataTableRow["data"][number]): value is DataTableCellObject =>
  typeof value === "object" && value !== null && Object.hasOwn(value, "value");

const getCellValue = (value: DataTableRow["data"][number]): ReactNode =>
  isDataTableCellObject(value) ? value.value : (value as ReactNode);

const getDefaultExpertOpen = (statType: View<"playerStats">["statType"]) =>
  !window.mobile || (statType !== "perGame" && statType !== "totals");

export const formatStatGameHigh = (ps: any, stat: string, statType?: string) => {
  if (stat.endsWith("Max")) {
    if (!Array.isArray(ps[stat])) {
      return null;
    }

    // Can be [max, gid] or (for career stats) [max, gid, abbrev, tid, season]
    const row = ps[stat] as unknown as [number, number] | [number, number, string, number, number];

    const abbrev = row.length > 3 ? row[2] : ps.abbrev;
    const tid = row.length > 3 ? row[3] : ps.tid;
    const season = row.length > 3 ? row[4] : ps.season;

    return (
      <a href={helpers.leagueUrl(["game_log", `${abbrev}_${tid}`, season as any, row[1]])}>
        {helpers.roundStat(row[0], stat, statType === "totals")}
      </a>
    );
  }

  if (stat === "pm100" || stat === "onOff100") {
    return <PlusMinus>{ps[stat]}</PlusMinus>;
  }

  return helpers.roundStat(ps[stat], stat, statType === "totals");
};

const PlayerStats = ({
  abbrev,
  players,
  playoffs,
  season,
  statType,
  stats,
  userTeamGamesPlayed,
  userTid,
}: View<"playerStats">) => {
  useTitleBar({
    title: i18n.t("playerStats.title", { ns: "ui", defaultValue: "Player Stats" }),
    jumpTo: !window.mobile,
    jumpToSeason: season,
    dropdownView: "player_stats",
    dropdownFields: {
      teamsAndAllWatchPlayoffs: abbrev,
      seasonsAndCareer: season,
      statTypesAdv: statType,
      playoffsCombined: playoffs,
    },
  });

  const [expertOpen, setExpertOpen] = useState(() => getDefaultExpertOpen(statType));

  // A stat-type route change carries fresh depth intent. Unrelated season/team
  // updates deliberately do not reset a user's manual disclosure choice.
  useEffect(() => {
    setExpertOpen(getDefaultExpertOpen(statType));
  }, [statType]);

  const visibleStats = filterPolicyKeys(stats, playerStatsDefaultStats);

  const cols = getCols([
    "Name",
    "Pos",
    "Age",
    "Team",
    ...(season === "all" ? ["Season"] : []),
    ...visibleStats.map((stat) =>
      stat === "pos" ? "Pos" : `stat:${stat.endsWith("Max") ? stat.replace("Max", "") : stat}`,
    ),
  ]);

  if (statType === "shotLocations" && visibleStats.length >= 7) {
    cols.at(-7)!.title = "M";
    cols.at(-6)!.title = "A";
    cols.at(-5)!.title = "%";
  }

  let sortCol = cols.length - 1;

  let statsProperty: "careerStats" | "careerStatsPlayoffs" | "careerStatsCombined" | "stats";
  if (season === "career") {
    statsProperty =
      playoffs === "playoffs"
        ? "careerStatsPlayoffs"
        : playoffs === "combined"
          ? "careerStatsCombined"
          : "careerStats";
  } else {
    statsProperty = "stats";
  }

  const getInsightStats = (p: (typeof players)[number]) =>
    (statsProperty === "stats" ? p.stats : p[statsProperty]) as Record<string, unknown>;
  const getInsightTid = (p: (typeof players)[number]) =>
    season === "career" ? p.tid : (getInsightStats(p).tid as number | undefined);
  const leagueLeadingMetrics = getP3V1LeagueLeadingMetrics(players, visibleStats, getInsightStats);
  const trendContext = getP3V1PlayerStatsTrendContext({
    playoffs,
    season,
    statType,
  });

  // A3: "data exists but is not meaningful yet" gate for the story area
  // (early season or zero top value). Historical scopes are never gated.
  const storyTopValue =
    leagueLeadingMetrics.length > 0
      ? Math.max(...leagueLeadingMetrics.map((metric) => metric.value))
      : undefined;
  const storyMeaningfulness = getLeaderboardMeaningfulness({
    topValue: storyTopValue,
    userTeamGamesPlayed,
  });
  const showStoryAccumulating =
    players.length > 0 &&
    !storyMeaningfulness.meaningful &&
    userTeamGamesPlayed !== undefined &&
    typeof season === "number";
  const playerStatsTeamState = abbrev === undefined || abbrev === "all" ? "all" : "team";
  const playerStatsAdvancedState =
    statType === "perGame" || statType === "totals" ? "standard" : "advanced";
  const isPlayerStatsAdvanced = playerStatsAdvancedState === "advanced";
  const isPlayerStatsTeamAdvanced = playerStatsTeamState === "team" && isPlayerStatsAdvanced;
  const p2s06FirstScreenMetricCount = isPlayerStatsTeamAdvanced ? 2 : 3;
  const firstScreenLeagueLeadingMetrics = isPlayerStatsAdvanced
    ? leagueLeadingMetrics.slice(0, p2s06FirstScreenMetricCount)
    : leagueLeadingMetrics;
  const playerStatsScopeValue =
    season === "career"
      ? i18n.t("playerStats.mobileScopeCareer", {
          ns: "ui",
          defaultValue: "Career stats",
        })
      : season === "all"
        ? i18n.t("playerStats.mobileScopeAll", {
            ns: "ui",
            defaultValue: "All seasons",
          })
        : playerStatsTeamState === "team"
          ? i18n.t("playerStats.mobileScopeTeamSeason", {
              ns: "ui",
              defaultValue: "{{season}} team stats",
              season,
            })
          : i18n.t("playerStats.mobileScopeSeason", {
              ns: "ui",
              defaultValue: "{{season}} season stats",
              season,
            });
  const playerStatsExplainer = playerStatsScopeValue;
  const playerStatBaseColCount = 4 + (season === "all" ? 1 : 0);
  const getPlayerStatLabel = (stat: string) => {
    const visibleStatIndex = visibleStats.indexOf(stat);
    return (
      cols[playerStatBaseColCount + visibleStatIndex]?.title ??
      cols[playerStatBaseColCount + visibleStatIndex]?.desc ??
      stat
    );
  };
  const renderPlayerName = (p: (typeof players)[number]) => {
    const insightStats = getInsightStats(p);
    const numericSeason = season === "career" ? undefined : (insightStats.season as number);

    return getCellValue(
      wrappedPlayerNameLabels({
        pid: p.pid,
        injury: p.injury,
        season: numericSeason,
        skills: p.ratings.skills,
        jerseyNumber: insightStats.jerseyNumber as string | undefined,
        defaultWatch: p.watch,
        firstName: p.firstName,
        firstNameShort: p.firstNameShort,
        lastName: p.lastName,
        awards: p.awards,
        awardsSeason: numericSeason,
        nameOnly: true,
      }),
    );
  };
  const renderPlayerStatMeta = (p: (typeof players)[number]) => {
    const insightStats = getInsightStats(p);
    const actualAbbrev = season === "career" ? p.abbrev : (insightStats.abbrev as string);
    const actualTid = season === "career" ? p.tid : (insightStats.tid as number);

    return (
      <>
        {getCellValue(wrappedPosition(p.pos))}{" "}
        <a
          href={helpers.leagueUrl([
            "roster",
            `${actualAbbrev}_${actualTid}`,
            ...(season === "career" ? [] : [insightStats.season as number]),
          ])}
        >
          <TeamAbbrevText tid={actualTid} abbrev={actualAbbrev} />
        </a>
      </>
    );
  };
  const formatMetricValue = (metric: { stat: string; value: number }) =>
    helpers.roundStat(metric.value, metric.stat, statType === "totals");

  // C4 data-story conclusion: the user team's scoring leader ranked against
  // the whole league (statInsights pure function; the league #1 emits nothing
  // because the leader row above already tells that story, 法则二). Only the
  // current-season per-game scope reads as "场均" — other scopes skip.
  const topScorerInsight =
    typeof season === "number" && statType === "perGame" && visibleStats.includes("pts")
      ? getTeamTopScorerInsight({
          gamesPlayed: userTeamGamesPlayed,
          getTid: getInsightTid,
          getValue: (p: (typeof players)[number]) =>
            getInsightStatValue(getInsightStats(p).pts),
          players,
          userTid,
        })
      : undefined;
  // R6-3: the first-screen "team's best" anchor uses the team's leading scorer
  // directly (not the league-rank-gated topScorerInsight above), so it still
  // shows for a weak team whose best scorer is nowhere near the league leaders.
  // Gated only on a meaningful per-game current-season scope, like the story.
  const userTeamBestScorer =
    typeof season === "number" &&
    statType === "perGame" &&
    visibleStats.includes("pts") &&
    storyMeaningfulness.meaningful
      ? getTeamTopScorer({
          getTid: getInsightTid,
          getValue: (p: (typeof players)[number]) =>
            getInsightStatValue(getInsightStats(p).pts),
          players,
          userTid,
        })
      : undefined;
  const formatInsightPts = (value: number) => helpers.roundStat(value, "pts");
  const getInsightPlayerName = (p: (typeof players)[number]) =>
    `${p.firstName} ${p.lastName}`;
  const topScorerInsightItems = topScorerInsight
    ? [
        {
          conclusion: `${i18n.t("statInsights.teamTopScorer", {
            ns: "ui",
            defaultValue: "{{name}} leads the team with {{value}} points per game",
            name: getInsightPlayerName(topScorerInsight.player),
            value: formatInsightPts(topScorerInsight.conclusion.value),
          })} · ${
            topScorerInsight.conclusion.placement.tied
              ? i18n.t("statInsights.leagueRankTied", {
                  ns: "ui",
                  defaultValue: "tied for league #{{rank}}",
                  rank: topScorerInsight.conclusion.placement.rank,
                })
              : i18n.t("statInsights.leagueRank", {
                  ns: "ui",
                  defaultValue: "league #{{rank}}",
                  rank: topScorerInsight.conclusion.placement.rank,
                })
          }`,
          evidence: topScorerInsight.conclusion.evidence.map((row) => (
            <MobileStatRow
              highlighted={row.isSubject}
              key={row.subject.pid}
              label={getInsightPlayerName(row.subject)}
              rank={`#${row.rank}`}
              value={formatInsightPts(row.value)}
            />
          )),
          id: "player-stats-team-top-scorer",
          tone: "positive" as const,
        },
      ]
    : [];

  const rows: DataTableRow[] = players.map((p) => {
    // HACKS to show right stats, info
    let actualAbbrev;
    let actualTid;
    if (season === "career") {
      actualAbbrev = p.abbrev;
      actualTid = p.tid;
    } else {
      actualAbbrev = p.stats.abbrev;
      actualTid = p.stats.tid;
    }
    if (statsProperty !== "stats") {
      p.stats = p[statsProperty];
    }

    const statsRow = visibleStats.map((stat) => formatStatGameHigh(p.stats, stat, statType));

    let key;
    if (season === "all") {
      key = `${p.pid}-${p.stats.season}`;
    } else {
      key = p.pid;
    }

    const numericSeason = season === "career" ? undefined : p.stats.season;

    return {
      key,
      metadata: {
        type: "player",
        pid: p.pid,
        season: season === "all" ? p.stats.season : season,
        playoffs,
      },
      data: [
        wrappedPlayerNameLabels({
          pid: p.pid,
          injury: p.injury,
          season: numericSeason,
          skills: p.ratings.skills,
          jerseyNumber: p.stats.jerseyNumber,
          defaultWatch: p.watch,
          firstName: p.firstName,
          firstNameShort: p.firstNameShort,
          lastName: p.lastName,
          awards: p.awards,
          awardsSeason: numericSeason,
          nameOnly: true,
        }),
        wrappedPosition(p.pos),

        // Only show age at death for career totals, otherwise just use current age
        season === "career" ? wrappedAgeAtDeath(p.age, p.ageAtDeath) : p.stats.season - p.born.year,

        <a
          href={helpers.leagueUrl([
            "roster",
            `${actualAbbrev}_${actualTid}`,
            ...(season === "career" ? [] : [p.stats.season]),
          ])}
        >
          <TeamAbbrevText tid={actualTid} abbrev={actualAbbrev} />
        </a>,

        ...(season === "all" ? [p.stats.season] : []),

        ...statsRow,
      ],
      classNames: {
        "table-danger": p.hof,
        "table-info": actualTid === userTid,
      },
    };
  });

  // B2a content-first migration (v5Content contract): the first screen IS the
  // top leaderboard rows (or the A3 accumulating line / empty reason). The
  // only narration is the single hero explainer line; scope chips, the data
  // story hero, and the insight strip are gone.
  return (
    <div
      data-host-state={players.length === 0 ? "empty" : "ready"}
      data-host-state-host="playerStats"
      data-host-state-row-count={players.length}
    >
        {/* G2e: the data-center hub tab strip retired; a single breadcrumb back to
            the converged Data entry (League Leaders) replaces it. */}
        <a
          className="btn btn-link btn-sm px-0 mb-2 d-inline-flex align-items-center gap-1"
          data-data-return-link=""
          href={helpers.leagueUrl(["leaders"])}
        >
          <span aria-hidden="true">←</span>
          {i18n.t("dataHub.returnToData", { ns: "ui", defaultValue: "Back to data" })}
        </a>

        <MobileContentHero
          className="mb-3"
          data-grouped-stat-selector={statType}
          data-season-data-story=""
          data-trend-or-change-insight={trendContext.mode}
          data-v5-playerstats-leader-rows-first-screen=""
          explainer={playerStatsExplainer}
        >
          {players.length === 0 ? (
            <div
              className="alert alert-warning mb-0"
            >
              {i18n.t("playerStats.noRowsReason", {
                ns: "ui",
                defaultValue:
                  "No players match the selected team, season, stat type, and split. If those filters have no games yet, play or simulate some first; otherwise switch filters.",
              })}
            </div>
          ) : null}
          <section
            data-league-leading-metrics={firstScreenLeagueLeadingMetrics.length}
          >
            {showStoryAccumulating && typeof season === "number" ? (
              <p
                className="text-body-secondary mb-0"
                data-a3-stat-accumulating=""
                data-a3-stat-accumulating-reason={
                  storyMeaningfulness.meaningful ? undefined : storyMeaningfulness.reason
                }
              >
                {getStatAccumulatingMessage(userTeamGamesPlayed ?? 0)}
                {" · "}
                <a
                  data-a3-previous-season-review=""
                  href={helpers.leagueUrl(["player_stats", abbrev ?? "all", season - 1])}
                >
                  {getPreviousSeasonReviewLabel(season - 1)}
                </a>
              </p>
            ) : (
              firstScreenLeagueLeadingMetrics.map((metric, index) => (
                <MobileEntityRow
                  key={`${metric.stat}-${index}`}
                  highlighted={getInsightTid(metric.player) === userTid}
                  rank={getPlayerStatLabel(metric.stat)}
                  title={renderPlayerName(metric.player)}
                  meta={renderPlayerStatMeta(metric.player)}
                  trailing={formatMetricValue(metric)}
                />
              ))
            )}
          </section>
          {/* R6-3: a first-screen "team's best" anchor so the user's own scoring
              leader reads at a glance, even when no rostered player cracks the
              league-leader rows above (playerRatings best-team-player precedent).
              The detailed league-rank evidence stays in the insight below. */}
          {userTeamBestScorer ? (
            <MobileStatRow
              className="mt-3"
              data-user-team-best-scorer=""
              detail={getInsightPlayerName(userTeamBestScorer.player)}
              highlighted
              label={i18n.t("playerStats.mobileUserTeamBestLabel", {
                ns: "ui",
                defaultValue: "Team's best",
              })}
              tone="positive"
              value={i18n.t("playerStats.mobileUserTeamBestValue", {
                ns: "ui",
                defaultValue: "{{value}} PPG",
                value: formatInsightPts(userTeamBestScorer.value),
              })}
            />
          ) : null}
        </MobileContentHero>

        {topScorerInsightItems.length > 0 ? (
          <MobileStatInsights
            className="mb-3"
            data-playerstats-stat-insights=""
            items={topScorerInsightItems}
          />
        ) : null}

        <MobileExpertDisclosure
          className="mb-3"
          count={i18n.t("playerStats.mobileAdvancedCount", {
            ns: "ui",
            defaultValue: "{{count}} players",
            count: rows.length,
          })}
          data-advanced-table-depth=""
          expertHostId="player-stats-complete-table"
          onToggle={(event) => {
            setExpertOpen((event.currentTarget as HTMLDetailsElement).open);
          }}
          open={expertOpen}
          summary={
            isPlayerStatsAdvanced
              ? i18n.t("playerStats.p2s06ExpertDataSummary", {
                  ns: "ui",
                  defaultValue: "Expert data: complete advanced table",
                })
              : i18n.t("playerStats.mobileAdvancedSummary", {
                  ns: "ui",
                  defaultValue: "Complete player stat table",
                })
          }
        >
          {/* B3: the DataTable is the single expert-depth carrier — the pruned
              card list on mobile, the unchanged table on desktop. The
              duplicate summary copy and the expert-mode real table are gone
              (法则二 / DataTable mobile exit). */}
          <DataTable
            cols={cols}
            defaultSort={[sortCol, "desc"]}
            defaultStickyCols={window.mobile ? 0 : 1}
            mobileP3Export={{
              label: i18n.t("playerStats.title", {
                ns: "ui",
                defaultValue: "Player Stats",
              }),
              season: typeof season === "number" ? season : undefined,
            }}
            name={`PlayerStats${statType}`}
            rows={rows}
            pagination
          />
        </MobileExpertDisclosure>

        <MoreLinks
          type="playerStats"
          page="player_stats"
          season={typeof season === "number" ? season : undefined}
          statType={statType}
          keepSelfLink
        />
      </div>
  );
};

export default PlayerStats;
